import { supabase } from '@/lib/supabase'

export interface PaymentMethodStatus {
  has_payment_method: boolean
  last4: string | null
  brand: string | null
  saved_at: string | null
}

interface BeginResponse {
  ok: true
  url: string
  nonce: string
}

interface RemoveResponse {
  ok: true
  removed: boolean
}

type VaultAction = 'begin' | 'status' | 'remove'

export class PaymentMethodApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message)
    this.name = 'PaymentMethodApiError'
  }
}

function readableMessage(action: VaultAction, status?: number): string {
  if (status === 401) return 'Your session has expired. Sign in again and retry.'
  if (status === 403) return 'You do not have permission to manage payment methods.'
  if (status === 422) return 'This organization is not linked to a billing customer account.'
  if (!status) return 'Could not reach the payment service. Check your connection and try again.'

  if (action === 'begin') return 'Could not start secure card setup. Please try again.'
  if (action === 'remove') return 'Could not remove the saved card. Please try again.'
  return 'Could not load the saved card status. Please try again.'
}

async function invokeVault<T>(action: VaultAction, orgId: string): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new PaymentMethodApiError(readableMessage(action, 401), 401)
  }

  const { data, error } = await supabase.functions.invoke<T>('save-customer-card', {
    body: { action, customer_org_id: orgId },
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (error) {
    const response = (error as { context?: Response }).context
    throw new PaymentMethodApiError(readableMessage(action, response?.status), response?.status)
  }

  if (!data || typeof data !== 'object' || !('ok' in data) || data.ok !== true) {
    throw new PaymentMethodApiError(readableMessage(action))
  }

  return data
}

export const paymentMethodsApi = {
  status: (orgId: string) => invokeVault<PaymentMethodStatus & { ok: true }>('status', orgId),
  begin: (orgId: string) => invokeVault<BeginResponse>('begin', orgId),
  remove: (orgId: string) => invokeVault<RemoveResponse>('remove', orgId),
}
